import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, delay, put, select, takeEvery } from 'typed-redux-saga';

import { ErrorPageType } from '../../../shared/components/error-page/types';
import { setErrorPageAction, type SetErrorPageActionPayload } from '../../../shared/features/config';
import { selectErrorPageReason, selectErrorPageType } from '../../../shared/features/config/selectors';
import { parentFrameCommunicator } from '../../../shared/features/parent-frame-communicator';
import { FATAL_EVENT_DELAY } from './constants';
import { Errors, OutgoingEvents } from './types';

const errorsHash: Partial<Record<ErrorPageType, Errors>> = {
    gone: Errors.EXPIRED_LINK,
    'not-found': Errors.INVALID_LINK,
    'unsupported-browser': Errors.UNSUPPORTED_BROWSER,
    'unsupported-touch': Errors.UNSUPPORTED_TOUCH,
    'unsupported-messenger': Errors.UNSUPPORTED_MESSENGER,
    'limit-users-count': Errors.LIMIT_USERS_COUNT,
} as const;

/**
 *
 */
function* handleServerError() {
    const type = yield* select(selectErrorPageType);
    const reason = yield* select(selectErrorPageReason);

    if (type) {
        yield* put(
            setErrorPageAction({
                type,
                reason: reason ?? {
                    method: 'handleServerError',
                },
            })
        );
    }
}

const getErrorCode = (errorType: ErrorPageType | undefined) => {
    if (errorType === undefined) {
        return Errors.UNKNOWN;
    }

    return errorsHash[errorType] ?? Errors.UNKNOWN;
};

/**
 * @param {Object} data
 */
function* handlePageError({ payload }: PayloadAction<SetErrorPageActionPayload>) {
    const errorPageType = payload?.type;

    const code = getErrorCode(errorPageType);
    if (code === Errors.UNSUPPORTED_MESSENGER) {
        return;
    }

    yield* delay(FATAL_EVENT_DELAY);

    yield* call(parentFrameCommunicator.sendMessage, '*', {
        event: OutgoingEvents.ERROR,
        payload: {
            value: { code },
        },
    });
}

/**
 *
 */
export function* handleErrors(): Generator {
    yield* all([call(handleServerError), takeEvery(setErrorPageAction, handlePageError)]);
}
